import { inject } from '@angular/core'
import { isString } from 'lodash'
import { Observable, Subscription } from 'rxjs'
import { AnonymousData } from '../../rest/decorators/mapped'
import { HttpService } from '../../rest/services/http.service'
import { RestQuery } from '../../rest/utilities/rest-query'
import { SerialisationConfig } from '../../rest/utilities/serialisable'
import {
  APIResponse,
  ApiValidationResult,
  PaginationMeta,
  PaginationResponse,
} from '../models/api-validation-result'
import { PaginatedList } from '../models/paginated-list'
import { EnvUtilities } from '../utilities/env-utilities'
import { SubscriptionService } from './subscription.service'

type MutationMethod = 'post' | 'put' | 'patch'
interface CrudRoute {
  method: MutationMethod
  path: string
  params?: Record<string, unknown>
}

export class ApiService {
  private subscriptionService = inject(SubscriptionService)

  async withSubscription<Response, Return>(
    request: Promise<Observable<Response>>,
    handleResponse: (response: Response) => Return,
    handleError: (error: Response) => Promise<never>,
    idempotencyKey?: string
  ): Promise<{ promise: Promise<Return>; subscription: Subscription | null }> {
    const observable = await request
    let subscription = null
    const promise = new Promise<Return>((resolve, reject) => {
      // noinspection JSUnusedGlobalSymbols
      subscription = observable.subscribe({
        next: (observedResponse) => {
          resolve(handleResponse(observedResponse))
        },
        error: (observedError) => reject(observedError),
        complete: () => {
          if (idempotencyKey) {
            this.subscriptionService.closed(idempotencyKey)
          }
        },
      })
      if (idempotencyKey) {
        this.subscriptionService.register(idempotencyKey, subscription)
      }
    }).catch((error_3) => handleError(error_3))
    return { promise, subscription }
  }

  error(response: APIResponse, config?: SerialisationConfig): Promise<never> {
    return Promise.reject(this.errorResult(response, config))
  }

  errorResult(
    response: APIResponse | undefined,
    config?: SerialisationConfig
  ): ApiValidationResult {
    if (!response) {
      return ApiValidationResult.withMessages([
        'Received no response from server',
      ])
    } else if (response.error instanceof ProgressEvent) {
      return ApiValidationResult.withMessages(['Problem reaching the server'])
    } else {
      return ApiValidationResult.generate(response, config)
    }
  }

  routeCrudRequest<T>(
    httpService: HttpService,
    getRoutes: (id: string | number | undefined) => {
      create: CrudRoute
      update: CrudRoute
      duplicate?: CrudRoute
    },
    data: unknown,
    query: RestQuery<unknown, unknown, unknown, unknown>,
    duplicate: boolean
  ): Promise<Observable<T>> {
    let id: string | number | undefined = undefined
    if (data) {
      if (data instanceof FormData) {
        const dataId = data.get('id')
        id = isString(dataId) ? dataId : undefined
      } else {
        id = (data as Record<string, string | number | undefined>)['id']
      }
    }

    const routes = getRoutes(id)
    let route: CrudRoute = routes.create
    if (id) {
      if (duplicate && routes.duplicate) {
        route = routes.duplicate
        if (data instanceof FormData) {
          data.set('id', '0')
        } else {
          ;(data as Record<string, unknown>)['id'] = 0
        }
      } else {
        route = routes.update
      }
    }
    return httpService[route.method](
      EnvUtilities.routeUrl(route.path, route.params, query),
      data
    )
  }

  protected makeListRequest<
    Model,
    ResponseMeta extends PaginationMeta,
    ListAdditional = undefined,
  >(
    request: Promise<Observable<APIResponse<AnonymousData[], ResponseMeta>>>,
    map: (response: APIResponse<AnonymousData[], ResponseMeta>) => Model[],
    additional?: (
      response: APIResponse<AnonymousData[], ResponseMeta>
    ) => ListAdditional
  ) {
    return this.withSubscription(
      request,
      (response) =>
        new PaginatedList<Model, ListAdditional>(
          map(response),
          response.meta.pagination.total,
          response.meta.pagination.per_page,
          response.meta.pagination.current_page,
          additional ? additional(response) : undefined
        ),
      (error) => this.error(error)
    )
  }

  protected makeLegacyListRequest<
    Model,
    ResponseMeta extends PaginationResponse,
    ListAdditional = undefined,
  >(
    request: Promise<Observable<APIResponse<AnonymousData[], ResponseMeta>>>,
    map: (response: APIResponse<AnonymousData[], ResponseMeta>) => Model[],
    additional?: (
      response: APIResponse<AnonymousData[], ResponseMeta>
    ) => ListAdditional
  ) {
    return this.withSubscription(
      request,
      (response) =>
        new PaginatedList<Model, ListAdditional>(
          map(response),
          response.meta.total,
          response.meta.per_page,
          response.meta.current_page,
          additional ? additional(response) : undefined
        ),
      (error) => this.error(error)
    )
  }
}
